import React from "react";
import { useNavigate } from "react-router-dom";
import { Award, Megaphone, PlayCircle, FileText, ArrowUpRight, Paperclip, Clock, Library, Sparkles, BookOpen, Target, ShieldCheck, HeartHandshake, PhoneCall, Users, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);
export const fmtDay = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
export const TYPE_LABEL = { leadership: "Leadership", announcement: "Announcement", article: "Article", recognition: "Recognition", training: "Training", resource: "Resource" };

export function ytId(url) {
  const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? m[1] : null;
}
export function videoThumb(item) {
  if (item.image_url) return mediaUrl(item.image_url);
  const id = ytId(item.video_url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export function SectionHead({ kicker, title, intro, action, onAction, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 sm:gap-3 mb-4">
      <div className="min-w-0 flex-1">
        {kicker && <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand/80">{kicker}</p>}
        <h2 className="font-editorial text-xl md:text-2xl text-slate-900 leading-tight">{title}</h2>
        {intro && <p className="mt-1 max-w-3xl text-sm text-slate-500 break-words" data-testid={testId ? `${testId}-intro` : undefined}>{intro}</p>}
      </div>
      {action && <button onClick={onAction} data-testid={testId} className="text-sm font-medium text-brand inline-flex items-center gap-1 hover:gap-1.5 transition-[gap] shrink-0">{action}<ArrowUpRight className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

export function QuietEmpty({ icon: Icon = Sparkles, text, testId }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-5 py-8 text-center" data-testid={testId}>
      <Icon className="w-5 h-5 text-slate-300 mx-auto mb-2" />
      <p className="text-sm text-slate-500">{text}</p>
    </div>
  );
}

export function NewsRow({ item, open, lead }) {
  const Icon = { article: BookOpen, recognition: Award, training: PlayCircle, resource: FileText, leadership: Sparkles }[item.type] || Megaphone;
  return (
    <button onClick={() => open(item)} data-testid={`news-item-${item.id}`}
      className={`group w-full text-left flex gap-4 py-4 border-b border-slate-100 last:border-0 ${lead ? "md:py-5" : ""}`}>
      {item.image_url ? (
        <img src={mediaUrl(item.image_url)} alt="" className={`${lead ? "w-28 h-20 md:w-36 md:h-24" : "w-16 h-16"} rounded-lg object-cover shrink-0`} />
      ) : (
        <span className={`${lead ? "w-12 h-12" : "w-10 h-10"} rounded-lg bg-brand/[0.07] text-brand flex items-center justify-center shrink-0`}><Icon className="w-5 h-5" /></span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400">
          <span className="font-semibold text-slate-500">{item.category || TYPE_LABEL[item.type]}</span><span>·</span><span>{fmtDay(item.published_on)}</span>
          {item.featured && <span className="rounded-full bg-amber-100 text-amber-700 px-1.5 normal-case tracking-normal font-semibold">Featured</span>}
        </span>
        <span className={`block font-semibold text-slate-900 group-hover:text-brand transition-colors ${lead ? "text-lg font-editorial" : "text-[15px]"}`}>{item.title}</span>
        {item.summary && <span className="block text-sm text-slate-500 line-clamp-2 mt-0.5">{item.summary}</span>}
        {item.attachment_count > 0 && <span className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><Paperclip className="w-3 h-3" />{item.attachment_count} attachment{item.attachment_count > 1 ? "s" : ""}</span>}
      </span>
    </button>
  );
}

export function RecognitionCard({ item, open }) {
  return (
    <button onClick={() => open(item)} data-testid={`recognition-${item.id}`}
      className="group snap-start shrink-0 w-[78%] sm:w-auto text-left rounded-2xl bg-white border border-slate-200 p-4 hover:border-amber-300 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
      <div className="flex items-center gap-3">
        {item.image_url ? <img src={mediaUrl(item.image_url)} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-200" />
          : <span className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-50 text-amber-600 flex items-center justify-center ring-2 ring-amber-200"><Award className="w-5 h-5" /></span>}
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">{item.category || "Recognition"}</p>
          <p className="font-semibold text-slate-900 truncate">{item.recognized_name || item.title}</p>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-600 line-clamp-2">{item.recognized_name ? item.title : item.summary}</p>
      <p className="mt-2 text-xs text-slate-400">{fmtDay(item.published_on)}</p>
    </button>
  );
}

export function TrainingCard({ item, open, compact }) {
  const thumb = videoThumb(item);
  return (
    <button onClick={() => open(item)} data-testid={`training-${item.id}`} className="group text-left w-full">
      <div className={`relative overflow-hidden rounded-xl bg-slate-900 ${compact ? "aspect-video" : "aspect-video"}`}>
        {thumb ? <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-[1.03] transition-transform duration-500" />
          : <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--brand-cyan)/0.55),transparent_60%)]" />}
        <span className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent" />
        {item.video_url ? <PlayCircle className="absolute left-3 bottom-3 w-9 h-9 text-white drop-shadow" />
          : <FileText className="absolute left-3 bottom-3 w-7 h-7 text-white drop-shadow" />}
        {item.duration_min ? <span className="absolute right-2 bottom-2 rounded bg-black/70 text-white text-[11px] px-1.5 py-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{item.duration_min} min</span> : null}
        {item.featured && <span className="absolute left-2 top-2 rounded-full bg-white/90 text-[10px] font-bold uppercase tracking-wider text-brand px-2 py-0.5">Featured</span>}
      </div>
      <p className="mt-2 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{item.category || "Training"}</p>
      <p className="font-semibold text-slate-900 group-hover:text-brand transition-colors leading-snug">{item.title}</p>
    </button>
  );
}

export function ResourceTile({ item, open }) {
  return (
    <button onClick={() => open(item)} data-testid={`resource-${item.id}`}
      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-brand/40 hover:shadow-sm transition-[border-color,box-shadow]">
      <span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors"><FileText className="w-5 h-5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-slate-900 truncate">{item.title}</span>
        <span className="block text-xs text-slate-500 truncate">{item.summary || item.category || "Resource"}</span>
      </span>
      <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-brand" />
    </button>
  );
}

const GROW_ICON = { target: Target, shield: ShieldCheck, heart: HeartHandshake, phone: PhoneCall, users: Users, book: BookOpen };

export function GrowCard({ item, open }) {
  const Icon = GROW_ICON[item.icon] || BookOpen;
  return (
    <button onClick={() => open(item)} data-testid={`grow-card-${item.id}`}
      className="group h-full text-left flex flex-col rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-brand/40 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200">
      <span className="w-11 h-11 rounded-xl bg-brand/[0.07] text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors"><Icon className="w-5 h-5" /></span>
      <span className="mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand/80">{item.category}</span>
      <span className="mt-1 font-editorial text-lg leading-snug text-slate-900 group-hover:text-brand transition-colors">{item.title}</span>
      <span className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-3">{item.summary}</span>
      <span className="mt-auto pt-4 text-sm font-semibold text-brand inline-flex items-center gap-1 group-hover:gap-2 transition-[gap]">Read Article <ArrowRight className="w-4 h-4" /></span>
    </button>
  );
}

export function ArchiveTeaser({ total, onOpen }) {
  return (
    <button onClick={onOpen} data-testid="archive-teaser"
      className="group w-full rounded-2xl bg-slate-900 text-white px-6 py-6 md:px-8 flex flex-col md:flex-row md:items-center gap-4 text-left overflow-hidden relative">
      <span className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-brand/40 blur-3xl" />
      <Library className="w-8 h-8 text-cyan-300 relative" />
      <span className="flex-1 relative">
        <span className="block font-editorial text-xl">The Archive</span>
        <span className="block text-sm text-white/70">Past leadership messages, memos, newsletters, guides and announcements — {total} published item{total === 1 ? "" : "s"}, searchable by category.</span>
      </span>
      <span className="relative inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 text-sm font-semibold px-4 py-2 group-hover:gap-2.5 transition-[gap]">Browse the archive <ArrowUpRight className="w-4 h-4" /></span>
    </button>
  );
}

export function useOpenContent(preview) {
  const navigate = useNavigate();
  return (item, state) => navigate(`/library/${item.id}${preview ? "?preview=agent" : ""}`, state ? { state } : undefined);
}
